import { type FormEvent, type ReactElement, useEffect, useState } from "react";
import { HTTPError } from "ky";

import {
  type Incident,
  type IncidentReviewAction,
  createIncident,
  deleteIncident,
  listIncidents,
  reviewIncident,
} from "@/api/incidents";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card } from "@/components/hifi/Card";
import { IconBtn } from "@/components/hifi/IconBtn";
import { Pill } from "@/components/hifi/Pill";
import { SevDot } from "@/components/hifi/misc";
import { ShellLayout } from "@/components/shell/ShellLayout";

const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const STATUSES = ["OPEN", "IN_PROGRESS", "CLOSED"];

type Draft = {
  title: string;
  description: string;
  severity: string;
  status: string;
};

const INITIAL_DRAFT: Draft = {
  title: "",
  description: "",
  severity: "MEDIUM",
  status: "OPEN",
};

function severityVariant(
  sev: string,
): "ok" | "warn" | "bad" | "accent" | "default" {
  if (sev === "CRITICAL") return "bad";
  if (sev === "HIGH") return "warn";
  if (sev === "MEDIUM") return "accent";
  return "ok";
}

function severityDot(
  sev: string,
): "critical" | "high" | "medium" | "low" | "info" {
  if (sev === "CRITICAL") return "critical";
  if (sev === "HIGH") return "high";
  if (sev === "MEDIUM") return "medium";
  return "low";
}

function reviewVariant(
  rs: string,
): "ok" | "accent" | "bad" | "warn" | "default" {
  if (rs === "APPROVED") return "ok";
  if (rs === "IN_REVIEW") return "accent";
  if (rs === "REJECTED") return "bad";
  if (rs === "PENDING") return "warn";
  return "default";
}

export function IncidentsPage(): ReactElement {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [draft, setDraft] = useState<Draft>(INITIAL_DRAFT);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      setIncidents(await listIncidents());
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Load failed (${String(err.response.status)})`
          : "Load failed",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createIncident({
        title: draft.title,
        description: draft.description,
        severity: draft.severity,
        status: draft.status,
        occurred_at: new Date().toISOString(),
      });
      setDraft(INITIAL_DRAFT);
      setShowForm(false);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Create failed (${String(err.response.status)})`
          : "Create failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!window.confirm("Delete this incident?")) return;
    try {
      await deleteIncident(id);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Delete failed (${String(err.response.status)})`
          : "Delete failed",
      );
    }
  }

  async function handleReview(
    id: string,
    action: IncidentReviewAction,
  ): Promise<void> {
    try {
      await reviewIncident(id, action);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Review ${action} failed (${String(err.response.status)})`
          : `Review ${action} failed`,
      );
    }
  }

  return (
    <ShellLayout
      active="incidents"
      topbar={{
        crumbs: ["Workspace", "Incidents"],
        title: "Incidents",
        subtitle: `${String(incidents.length)} tracked`,
        right: (
          <Btn2
            variant="accent"
            size="sm"
            icon="plus"
            onClick={() => {
              setShowForm((v) => !v);
            }}
          >
            {showForm ? "Cancel" : "Log incident"}
          </Btn2>
        ),
      }}
    >
      <div className="space-y-5 p-5">
        {error && (
          <div
            role="alert"
            className="rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
          >
            {error}
          </div>
        )}

        {showForm && (
          <Card>
            <div className="border-b border-n-150 px-4 py-2.5">
              <div className="text-[13px] font-semibold text-n-900">
                Log incident
              </div>
            </div>
            <form
              onSubmit={(e) => {
                void handleCreate(e);
              }}
              className="grid grid-cols-1 gap-3 p-4 md:grid-cols-6"
              noValidate
            >
              <label className="block text-[11px] font-medium text-n-600 md:col-span-3">
                Title
                <input
                  value={draft.title}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, title: e.target.value }));
                  }}
                  required
                  minLength={1}
                  maxLength={200}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <label className="block text-[11px] font-medium text-n-600">
                Severity
                <select
                  value={draft.severity}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, severity: e.target.value }));
                  }}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] font-medium text-n-600">
                Status
                <select
                  value={draft.status}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, status: e.target.value }));
                  }}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] font-medium text-n-600 md:col-span-6">
                Description
                <textarea
                  value={draft.description}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, description: e.target.value }));
                  }}
                  rows={2}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <div className="md:col-span-6">
                <Btn2 variant="accent" type="submit" disabled={submitting}>
                  {submitting ? "Logging…" : "Log incident"}
                </Btn2>
              </div>
            </form>
          </Card>
        )}

        <Card>
          {loading ? (
            <p className="p-4 text-[13px] text-n-500">Loading…</p>
          ) : incidents.length === 0 ? (
            <p className="p-4 text-[13px] text-n-500">
              No incidents yet. Log one above.
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Severity</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Review</th>
                  <th className="px-4 py-2">Occurred</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {incidents.map((i) => (
                  <tr
                    key={i.id}
                    className="border-b border-n-100 align-top last:border-0 hover:bg-n-50"
                  >
                    <td className="px-4 py-2.5 font-medium text-n-900">
                      <div className="flex items-start gap-2">
                        <div className="mt-1.5">
                          <SevDot sev={severityDot(i.severity)} />
                        </div>
                        <div>
                          <div>{i.title}</div>
                          {i.description && (
                            <p className="mt-0.5 text-[11px] font-normal text-n-500">
                              {i.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant={severityVariant(i.severity)}>
                        {i.severity}
                      </Pill>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                      {i.status}
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant={reviewVariant(i.review_status)}>
                        {i.review_status}
                      </Pill>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-600">
                      {new Date(i.occurred_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {i.review_status === "PENDING" && (
                          <Btn2
                            size="xs"
                            variant="accent"
                            onClick={() => {
                              void handleReview(i.id, "submit");
                            }}
                          >
                            Submit
                          </Btn2>
                        )}
                        {i.review_status === "IN_REVIEW" && (
                          <>
                            <Btn2
                              size="xs"
                              variant="accent"
                              onClick={() => {
                                void handleReview(i.id, "approve");
                              }}
                            >
                              Approve
                            </Btn2>
                            <Btn2
                              size="xs"
                              variant="danger"
                              onClick={() => {
                                void handleReview(i.id, "reject");
                              }}
                            >
                              Reject
                            </Btn2>
                          </>
                        )}
                        {i.review_status === "REJECTED" && (
                          <Btn2
                            size="xs"
                            onClick={() => {
                              void handleReview(i.id, "submit");
                            }}
                          >
                            Re-submit
                          </Btn2>
                        )}
                        <IconBtn
                          name="x"
                          size="sm"
                          label="Delete"
                          onClick={() => {
                            void handleDelete(i.id);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </ShellLayout>
  );
}
